import React, { useState, useEffect, useCallback, useMemo } from "react";
import { GetAll, BulkCheckout } from "adminApi/RoomCheCkOutApi/RoomCheCkOut.js";
import DataTable from "Common/DataTable/DataTable";
import useDebouncedValue from "Common/DataTable/useDebouncedValue";
import useTableConfig from "Hooks/useTableConfig";
import { generateDisplayFields } from "utils/fieldMapper";
import feilds from "../../json/RoomCheckOutFields.json";

import { showSuccess, showError } from "utils/notifications";


const ID_KEY = "booked_id";

const CheckoutListTable = ({ filters, date }) => {
  const [search, setSearch] = useState("");
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkLoading, setBulkLoading] = useState(false);
  const debouncedSearch = useDebouncedValue(search, 500);
  const { tabledata } = feilds;

  const fetchCheckouts = useCallback(async (signal) => {
    setLoading(true);
    setError(null);

    try {
      const res = await GetAll(signal, filters);

      if (res.success) {
        setData(res.data || []);
      } else {
        setError(res.message || "Failed to fetch checkout list");
      }
    } catch (err) {
      setError(err.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    const controller = new AbortController();
    fetchCheckouts(controller.signal);
    return () => controller.abort();
  }, [fetchCheckouts]);

  const refetch = () => fetchCheckouts();

  const filteredData = debouncedSearch
    ? data.filter((row) =>
      Object.values(row).some((val) =>
        String(val ?? "")
          .toLowerCase()
          .includes(debouncedSearch.toLowerCase())
      )
    )
    : data;

  const displayFields = generateDisplayFields(tabledata);

  // ---- Row selection helpers ----
  const allVisibleIds = useMemo(
    () => filteredData.map((row) => row[ID_KEY]),
    [filteredData]
  );

  const isAllSelected =
    allVisibleIds.length > 0 &&
    allVisibleIds.every((id) => selectedIds.includes(id));

  const isSomeSelected = selectedIds.length > 0 && !isAllSelected;

  const toggleSelectAll = () => {
    if (isAllSelected) {
      setSelectedIds((prev) => prev.filter((id) => !allVisibleIds.includes(id)));
    } else {
      setSelectedIds((prev) => Array.from(new Set([...prev, ...allVisibleIds])));
    }
  };

  const toggleSelectRow = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((sid) => sid !== id) : [...prev, id]
    );
  };

  // ---- Bulk checkout: now calls the real API ----
  const handleBulkCheckout = async () => {
    if (selectedIds.length === 0) return;

    setBulkLoading(true);
    try {
      const res = await BulkCheckout(selectedIds);

      if (res.success) {
        const results = res.data?.results || [];
        const successIds = results
          .filter((r) => r.status === "SUCCESS")
          .map((r) => r.bookingId);
        const failed = results.filter((r) => r.status !== "SUCCESS");

        if (successIds.length > 0) {
          showSuccess(`${successIds.length} booking(s) checked out successfully.`);
        }

        if (failed.length > 0) {
          failed.forEach((f) =>
            showError(`Booking ${f.bookingId} failed: ${f.status}`)
          );
        }

        setSelectedIds([]);
        refetch(); // re-pull the today list; checked-out rows will drop off naturally
      } else {
        showError(res.message || "Bulk checkout failed.");
      }
    } catch (err) {
      showError(err.message || "Bulk checkout failed.");
    } finally {
      setBulkLoading(false);
    }
  };

  const { formattedData, columns } = useTableConfig({
    data: filteredData,
    canShowAction: false,
    refetch,
    config: {
      entityName: "Checkout",
      displayFields,
      idKey: ID_KEY,
    },
  });

  const selectionColumn = {
    id: "select",
    accessor: "select",
    Header: (
      <input
        type="checkbox"
        checked={isAllSelected}
        ref={(el) => {
          if (el) el.indeterminate = isSomeSelected;
        }}
        onChange={toggleSelectAll}
      />
    ),
    Cell: ({ row }) => {
      const rowId = row.original[ID_KEY];
      return (
        <input
          type="checkbox"
          checked={selectedIds.includes(rowId)}
          onChange={() => toggleSelectRow(rowId)}
        />
      );
    },
  };

  const tableColumns = [...columns, selectionColumn];

  const handleSearch = (val) => {
    setSearch(val);
  };

  return (
    <>
      {selectedIds.length > 0 && (
        <div className="col-12 d-flex justify-content-end mb-2">
          <span className="me-2 align-self-center text-muted">
            {selectedIds.length} selected
          </span>
          <button
            className="btn btn-primary"
            onClick={handleBulkCheckout}
            disabled={bulkLoading}
          >
            {bulkLoading ? "Checking out..." : "Bulk Checkout"}
          </button>
        </div>
      )}

      <DataTable
        columns={tableColumns}
        data={formattedData}
        options={{
          loading: loading || bulkLoading,
          error,
          sortableColumns: displayFields.map((f) => f.accessor),
          copy: true,
          csv: true,
          pdf: true,
          print: true,
          fileName: "Today Checkout List",
        }}
        Search={{
          search: false,
          SearchInputValue: search,
          handleSearch: handleSearch,
        }}
        pagination={{
          isActive: false,
        }}
        isDate={true}
        date={date}
      />
    </>
  );
};

export default CheckoutListTable;